// The organisation's Venues screen (spec/13 §13, F-ORG-01; ADR-0019 Phase 2, F-ORG-17), as TanStack
// Query hooks over the generated client — `packages/contracts/src/routes/{venues,franchisees}.ts`:
//
//   GET  /v1/organizations/{o}/venues                     the organisation's venues      venues:read
//   POST /v1/organizations/{o}/venues                     create one, in `setup`         venues:create
//   GET  /v1/organizations/{o}/venues/{v}                 one, with its ETag             venues:read
//   POST /v1/organizations/{o}/venues/{v}/archive         close it for good, If-Match    venues:archive
//   GET  /v1/organizations/{o}/franchisees                its direct franchisees         franchise:read
//   GET  /v1/organizations/{o}/franchisees/{f}/venues     one franchisee's venues        franchise:read

import type { CreateVenue, Franchisee, Venue } from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';
import { queryKeys } from '../../api/queries';
import { versioned } from '../../api/versioned';

export const venueListKeys = {
  list: (organizationId: string) => ['org-venues', organizationId, 'list'] as const,
  franchisees: (organizationId: string) => ['org-venues', organizationId, 'franchisees'] as const,
  franchiseeVenues: (organizationId: string, franchiseeId: string) => ['org-venues', organizationId, 'franchisee', franchiseeId] as const,
};

/** The organisation's venues, archived ones left out. The list is short; the first page is all of it. */
export function useOrganizationVenues(organizationId: string, enabled: boolean) {
  const api = useApi();
  return useQuery<readonly Venue[]>({
    queryKey: venueListKeys.list(organizationId),
    enabled,
    queryFn: async () => (await api.listVenues({ path: { organizationId }, query: { limit: 100 } })).data,
  });
}

/** After a venue is made or archived, the list and the member's own venues (the switcher) are stale. */
function useInvalidateVenues(organizationId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: venueListKeys.list(organizationId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.myVenues });
  };
}

/** Create a venue; it starts in `setup` with the organisation's currency. */
export function useCreateVenue(organizationId: string) {
  const api = useApi();
  const invalidate = useInvalidateVenues(organizationId);
  return useMutation({
    mutationFn: (body: CreateVenue) => api.createVenue({ path: { organizationId }, body }),
    onSuccess: invalidate,
  });
}

/** Archive a venue: read its ETag, then close it with `If-Match` (spec/08 §2.5). */
export function useArchiveVenue(organizationId: string) {
  const api = useApi();
  const invalidate = useInvalidateVenues(organizationId);
  return useMutation({
    mutationFn: async (venueId: string) => {
      const { etag } = await versioned((onResponse) => api.getVenue({ path: { organizationId, venueId }, onResponse }));
      return api.archiveVenue({ path: { organizationId, venueId }, headerParams: { 'if-match': etag } });
    },
    onSuccess: invalidate,
  });
}

/** The organisation's direct franchisees, when it is a franchisor and the member may read them. */
export function useFranchisees(organizationId: string, enabled: boolean) {
  const api = useApi();
  return useQuery<readonly Franchisee[]>({
    queryKey: venueListKeys.franchisees(organizationId),
    enabled,
    queryFn: async () => (await api.listFranchisees({ path: { organizationId }, query: { limit: 100 } })).data,
  });
}

/** One franchisee's venues, read-only (ADR-0019 Phase 2). */
export function useFranchiseeVenues(organizationId: string, franchiseeId: string) {
  const api = useApi();
  return useQuery<readonly Venue[]>({
    queryKey: venueListKeys.franchiseeVenues(organizationId, franchiseeId),
    queryFn: async () => (await api.listFranchiseeVenues({ path: { organizationId, franchiseeId }, query: { limit: 100 } })).data,
  });
}
